import React, { useCallback, useEffect, useState } from 'react';
import { FiSave, FiRefreshCw } from 'react-icons/fi';
import AdminLayout from '../components/AdminLayout';
import { AdminPageHeader, formatArs } from '../components/admin';
import { FormAlert, FormField, formInputClass } from '../components/form';
import { useApi } from '../hooks/useApi';
import useAdminPermissions from '../hooks/useAdminPermissions';
import { useThemeClasses } from '../hooks/useThemeClasses';

const AdminFiscal = () => {
  const tc = useThemeClasses();
  const { secureFetch } = useApi();
  const { canManageSystem } = useAdminPermissions();

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await secureFetch('/api/admin/fiscal/parametria');
      if (!res.ok) throw new Error('No se pudo leer la parametría fiscal.');
      const data = await res.json();
      setForm({
        fuente: data.fuente || '',
        urlReferencia: data.urlReferencia || '',
        vigenciaDesde: data.vigenciaDesde || '',
        vigenciaHasta: data.vigenciaHasta || '',
        disclaimer: data.disclaimer || '',
        categorias: (data.categorias || []).map((c) => ({ ...c })),
        actualizadoEn: data.actualizadoEn || null,
      });
    } catch (err) {
      setError(err.message || 'Error al cargar.');
    } finally {
      setLoading(false);
    }
  }, [secureFetch]);

  useEffect(() => {
    load();
  }, [load]);

  const updateMeta = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setSuccess('');
  };

  const updateCat = (idx, field, value) => {
    setForm((prev) => {
      const categorias = prev.categorias.map((c, i) => (i === idx ? { ...c, [field]: value } : c));
      return { ...prev, categorias };
    });
    setSuccess('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        ...form,
        categorias: form.categorias.map((c) => ({
          codigo: c.codigo,
          facturacionMaxAnual: Number(c.facturacionMaxAnual),
          cuotaServicios: Number(c.cuotaServicios),
          cuotaBienes: Number(c.cuotaBienes),
        })),
      };
      const res = await secureFetch('/api/admin/fiscal/parametria', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar.');
      setSuccess(data.message);
      if (data.parametria) {
        setForm({
          ...data.parametria,
          categorias: (data.parametria.categorias || []).map((c) => ({ ...c })),
        });
      }
    } catch (err) {
      setError(err.message || 'Error al guardar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout requiredPermission={canManageSystem}>
      <div className="max-w-7xl mx-auto py-6 space-y-5">
        <AdminPageHeader
          title="Parametría fiscal"
          subtitle="Escalas de Monotributo que usan Reportes e Inicio. Se aplican al guardar, sin redeploy."
          actions={
            <button type="button" onClick={load} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs ${tc.btnOutline}`}>
              <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Recargar
            </button>
          }
        />

        {error && <FormAlert type="error">{error}</FormAlert>}
        {success && <FormAlert type="success">{success}</FormAlert>}

        {loading || !form ? (
          <div className={`rounded-2xl border p-8 text-center ${tc.box}`}>
            <p className={tc.textMuted}>Cargando escalas…</p>
          </div>
        ) : (
          <form onSubmit={handleSave} className="space-y-4 pb-20">
            <div className={`rounded-2xl border p-4 sm:p-5 ${tc.box}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormField label="Vigencia desde" labelClass={tc.labelClass}>
                  <input type="date" value={form.vigenciaDesde || ''} onChange={(e) => updateMeta('vigenciaDesde', e.target.value)} className={formInputClass(tc.inputClass, { compact: true })} />
                </FormField>
                <FormField label="Vigencia hasta" labelClass={tc.labelClass}>
                  <input type="date" value={form.vigenciaHasta || ''} onChange={(e) => updateMeta('vigenciaHasta', e.target.value)} className={formInputClass(tc.inputClass, { compact: true })} />
                </FormField>
                <FormField label="Fuente" className="sm:col-span-2" labelClass={tc.labelClass}>
                  <input value={form.fuente || ''} onChange={(e) => updateMeta('fuente', e.target.value)} className={formInputClass(tc.inputClass, { compact: true })} />
                </FormField>
                <FormField label="URL de referencia" className="sm:col-span-2" labelClass={tc.labelClass}>
                  <input value={form.urlReferencia || ''} onChange={(e) => updateMeta('urlReferencia', e.target.value)} className={formInputClass(tc.inputClass, { compact: true })} />
                </FormField>
                <FormField label="Disclaimer" className="sm:col-span-2" labelClass={tc.labelClass}>
                  <textarea value={form.disclaimer || ''} onChange={(e) => updateMeta('disclaimer', e.target.value)} rows={2} className={formInputClass(tc.inputClass, { compact: true })} />
                </FormField>
              </div>
              {form.actualizadoEn && (
                <p className={`text-[11px] mt-3 ${tc.textMuted}`}>
                  Última actualización: {new Date(form.actualizadoEn).toLocaleString('es-AR')}
                </p>
              )}
            </div>

            <div className={`rounded-2xl border overflow-hidden ${tc.box}`}>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-sm">
                  <thead className={tc.modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-700'}>
                    <tr className="text-[11px] uppercase tracking-wide">
                      <th className="text-left px-3 py-2.5 font-medium">Cat.</th>
                      <th className="text-right px-3 py-2.5 font-medium">Tope anual</th>
                      <th className="text-right px-3 py-2.5 font-medium">Cuota servicios</th>
                      <th className="text-right px-3 py-2.5 font-medium">Cuota bienes</th>
                    </tr>
                  </thead>
                  <tbody className={tc.modoOscuro ? 'divide-y divide-gray-800' : 'divide-y divide-gray-100'}>
                    {form.categorias.map((c, idx) => (
                      <tr key={c.codigo}>
                        <td className={`px-3 py-2 font-semibold ${tc.textPri}`}>{c.codigo}</td>
                        {['facturacionMaxAnual', 'cuotaServicios', 'cuotaBienes'].map((field) => (
                          <td key={field} className="px-3 py-1.5">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={c[field] ?? ''}
                              onChange={(e) => updateCat(idx, field, e.target.value)}
                              className={`w-full text-right rounded-lg border px-2 py-1.5 tabular-nums ${tc.inputClass}`}
                            />
                            <p className={`text-[10px] text-right mt-0.5 tabular-nums ${tc.textMuted}`}>
                              {formatArs(c[field])}
                            </p>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className={`sticky bottom-3 z-10 rounded-2xl border px-4 py-3 flex flex-wrap items-center justify-between gap-3 ${tc.box}`}>
              <p className={`text-xs ${tc.textMuted}`}>
                Los cambios se aplican de inmediato a Reportes e Inicio.
              </p>
              <button
                type="submit"
                disabled={saving}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold disabled:opacity-50 ${tc.btnPrimary}`}
              >
                <FiSave className="w-4 h-4" />
                {saving ? 'Guardando…' : 'Guardar escalas'}
              </button>
            </div>
          </form>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminFiscal;
